import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Bot, Send, Sparkles, X, AlertTriangle } from 'lucide-react';
import { GoogleGenerativeAI } from '@google/generative-ai';
import { data } from '../data/data';

// ─── Constantes ──────────────────────────────────────────────────────────────

const CATEGORY_CHIPS = [
  { label: 'Salud', topicId: 'salud-y-bienestar-social' },
  { label: 'Educación', topicId: 'educacion' },
  { label: 'Seguridad', topicId: 'seguridad-y-narcotrafico' },
  { label: 'Economía', topicId: 'economia-y-desarrollo-rural' },
  { label: 'Corrupción', topicId: 'corrupcion' },
  { label: 'Energía', topicId: 'medio-ambiente-y-energia' },
  { label: 'Inclusión', topicId: 'inclusion-mujer-y-minorias' }
];

const SYSTEM_PROMPT = `Eres "Asistente Voto Consciente 2026", un asistente electoral de Colombia diseñado para ayudar a los ciudadanos a comparar propuestas presidenciales de forma OBJETIVA, clara y sin sesgos.

Tu ÚNICA fuente de información son los siguientes datos JSON oficiales sobre los planes de gobierno:

${JSON.stringify(data, null, 2)}

REGLAS DE ORO que debes seguir sin excepción:

1. EQUILIBRIO OBLIGATORIO: Si mencionas a Iván Cepeda en un tema, DEBES mencionar también la posición de Abelardo de la Espriella en ese mismo tema, y viceversa. Nunca dejes un candidato sin su contraparte.

2. VOCABULARIO NEUTRAL: Está PROHIBIDO usar adjetivos valorativos como "mejor", "peor", "peligroso", "excelente", "bueno" o "malo" para describir propuestas. Usa siempre verbos descriptivos: "Propone...", "Plantea...", "Busca...", "Establece...", "Contempla...", "Prioriza...".

3. SOLO LOS DATOS: Responde únicamente basándote en los datos JSON proporcionados. No inventes ni inferesposiciones. Si un tema no aparece en los datos, responde: "No tengo información sobre ese tema en los planes de gobierno registrados en esta plataforma. Te invito a preguntar sobre educación, salud, economía, seguridad, corrupción, energía o inclusión."

4. REDIRECCIÓN AMABLE: Si el usuario hace preguntas fuera del contexto electoral, usa lenguaje ofensivo o busca debatir temas ajenos, responde con cordialidad: "Entiendo tu inquietud, pero mi misión es ayudarte a comparar las propuestas electorales de forma objetiva. ¿Te gustaría saber qué propone cada candidato sobre [tema relevante]?"

5. FORMATO DE RESPUESTA: Estructura tus respuestas así:
   - Una frase introductoria del tema.
   - 🔵 Iván Cepeda: [descripción neutral de su propuesta]
   - ⚪ Abelardo de la Espriella: [descripción neutral de su propuesta]
   - Una frase de cierre invitando al usuario a profundizar si lo desea.

6. IDIOMA: Responde siempre en español colombiano, con un tono cercano, respetuoso e informativo.`;

const ERROR_REPLY = 'Ocurrió un error al contactar la IA. Verifica tu clave API y vuelve a intentarlo.';

const NO_API_KEY_REPLY =
  'No se encontró la clave de API de Gemini (REACT_APP_GEMINI_API_KEY). ' +
  'Agrégala en el archivo .env y reinicia el servidor de desarrollo.';

const panelVariants = {
  initial: { opacity: 0, y: 20, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 16, scale: 0.98 }
};

const panelTransition = { duration: 0.22, ease: 'easeOut' };

// ─── Utilidades Gemini ────────────────────────────────────────────────────────

/**
 * Inicializa el cliente Gemini y devuelve el modelo de chat
 * con el historial de conversación actual como contexto.
 */
const buildGeminiChat = (conversationHistory) => {
  const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
  if (!apiKey || apiKey === 'your_gemini_api_key_here') {
    return null;
  }

  const genAI = new GoogleGenerativeAI(apiKey);
  const model = genAI.getGenerativeModel({ model: 'gemini-1.5-flash' });

  // Convierte el historial de mensajes al formato que espera Gemini
  const geminiHistory = conversationHistory
    .filter((msg) => msg.id !== 'msg-welcome') // Omitir el mensaje de bienvenida
    .map((msg) => ({
      role: msg.role === 'user' ? 'user' : 'model',
      parts: [{ text: msg.text }]
    }));

  return model.startChat({
    history: geminiHistory,
    generationConfig: {
      maxOutputTokens: 800,
      temperature: 0.4 // Más determinista para respuestas electorales
    }
  });
};

/**
 * Envía una pregunta a Gemini con el system prompt inyectado al inicio.
 * Retorna el texto de la respuesta o lanza un error.
 */
const askGemini = async (userQuestion, conversationHistory) => {
  const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
  if (!apiKey || apiKey === 'your_gemini_api_key_here') {
    return NO_API_KEY_REPLY;
  }

  const chat = buildGeminiChat(conversationHistory);
  if (!chat) {
    return NO_API_KEY_REPLY;
  }

  // El system prompt va en el primer mensaje si el historial está vacío
  const messageToSend =
    conversationHistory.filter((m) => m.id !== 'msg-welcome').length === 0
      ? `${SYSTEM_PROMPT}\n\nPregunta del usuario: ${userQuestion}`
      : userQuestion;

  const result = await chat.sendMessage(messageToSend);
  const response = await result.response;
  return response.text();
};

// ─── Subcomponentes ───────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="flex max-w-[85%] items-center gap-3 rounded-3xl rounded-bl-md border border-slate-100 bg-white px-4 py-3 text-sm text-slate-500 shadow-sm sm:max-w-[78%]">
        <span className="sr-only">Gemini está respondiendo</span>
        <span className="flex items-center gap-1" aria-hidden="true">
          <span className="h-2 w-2 animate-bounce rounded-full bg-indigo-400 [animation-delay:0ms]" />
          <span className="h-2 w-2 animate-bounce rounded-full bg-violet-400 [animation-delay:150ms]" />
          <span className="h-2 w-2 animate-bounce rounded-full bg-purple-400 [animation-delay:300ms]" />
        </span>
        <span className="font-medium">Gemini está analizando...</span>
      </div>
    </div>
  );
}

function ApiKeyWarning() {
  return (
    <div className="mx-4 mt-3 flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
      <span>
        Agrega tu clave <strong>REACT_APP_GEMINI_API_KEY</strong> en el archivo{' '}
        <code className="rounded bg-amber-100 px-1">.env</code> y reinicia.
      </span>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

const INITIAL_MESSAGE = {
  id: 'msg-welcome',
  role: 'assistant',
  text: '¡Hola! Soy el Asistente Voto Consciente 2026 🇨🇴, impulsado por Google Gemini.\n\nEstoy aquí para ayudarte a comparar las propuestas de Iván Cepeda y Abelardo de la Espriella de forma objetiva y equilibrada, basándome únicamente en sus planes de gobierno oficiales.\n\nPuedes elegir un tema abajo o hacerme una pregunta directa. Siempre te daré la posición de ambos candidatos.'
};

export default function ChatAsistente({ open, onClose }) {
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(true);
  const bottomRef = useRef(null);
  const messageIdRef = useRef(1);

  // Verifica la API key al montar
  useEffect(() => {
    const key = process.env.REACT_APP_GEMINI_API_KEY;
    setHasApiKey(Boolean(key) && key !== 'your_gemini_api_key_here');
  }, []);

  // Auto-scroll al último mensaje
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading, open]);

  const createMessage = useCallback((role, text) => {
    messageIdRef.current += 1;
    return { id: `msg-${messageIdRef.current}`, role, text };
  }, []);

  const categoryChips = useMemo(
    () =>
      CATEGORY_CHIPS.map((chip) => ({
        ...chip,
        topic: data.find((item) => item.id === chip.topicId) || null
      })).filter((chip) => chip.topic),
    []
  );

  /**
   * Función principal que envía una pregunta a Gemini y
   * agrega la respuesta al historial de mensajes del chat.
   */
  const enviarPregunta = useCallback(
    async (userText) => {
      if (!userText?.trim() || isLoading) return;

      const userMessage = createMessage('user', userText);
      const updatedHistory = [...messages, userMessage];

      setMessages(updatedHistory);
      setIsLoading(true);

      try {
        const reply = await askGemini(userText, updatedHistory);
        setMessages((current) => [...current, createMessage('assistant', reply)]);
      } catch (error) {
        console.error('Error al consultar Gemini:', error);
        setMessages((current) => [
          ...current,
          createMessage('assistant', ERROR_REPLY)
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [messages, isLoading, createMessage]
  );

  const handleCategorySelect = useCallback(
    (chip) => {
      if (isLoading) return;
      const pregunta = `¿Cuál es la propuesta de cada candidato en ${chip.label}?`;
      enviarPregunta(pregunta);
    },
    [isLoading, enviarPregunta]
  );

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      const question = inputValue.trim();
      if (!question || isLoading) return;
      setInputValue('');
      enviarPregunta(question);
    },
    [inputValue, isLoading, enviarPregunta]
  );

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit(event);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/50 px-3 py-3 backdrop-blur-sm sm:items-center sm:px-4 sm:py-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="chat-asistente-title"
        >
          {/* Fondo clickeable para cerrar */}
          <button
            type="button"
            className="absolute inset-0 cursor-default border-0 bg-transparent"
            aria-label="Cerrar asistente"
            onClick={onClose}
          />

          <motion.div
            variants={panelVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={panelTransition}
            className="relative z-10 flex w-full max-w-xl flex-col overflow-hidden rounded-[2rem] border border-white/40 bg-white shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-600 px-5 py-4 text-white sm:px-6">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 backdrop-blur">
                  <Bot className="h-6 w-6" />
                </div>
                <div>
                  <p id="chat-asistente-title" className="text-sm font-semibold">
                    Asistente Electoral IA
                  </p>
                  <p className="mt-0.5 text-xs text-white/70">
                    Impulsado por Google Gemini · Solo datos oficiales
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                aria-label="Cerrar chat"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Aviso de API key faltante */}
            {!hasApiKey && <ApiKeyWarning />}

            {/* Chips de categorías */}
            <div className="border-b border-slate-100 bg-slate-50 px-5 py-3 sm:px-6">
              <div className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                <Sparkles className="h-3.5 w-3.5" />
                Acceso rápido por tema
              </div>
              <div className="flex flex-wrap gap-2">
                {categoryChips.map((chip) => (
                  <button
                    key={chip.topicId}
                    type="button"
                    disabled={isLoading}
                    onClick={() => handleCategorySelect(chip)}
                    className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 ring-1 ring-slate-200 transition-colors hover:bg-slate-900 hover:text-white hover:ring-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Área de mensajes */}
            <div className="max-h-[58vh] flex-1 space-y-4 overflow-y-auto bg-gradient-to-b from-slate-50 to-white px-4 py-5 sm:px-6">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] whitespace-pre-line rounded-3xl px-4 py-3 text-sm leading-7 shadow-sm sm:max-w-[78%] ${
                      message.role === 'user'
                        ? 'rounded-br-md bg-gradient-to-br from-indigo-600 to-purple-600 text-white'
                        : 'rounded-bl-md border border-slate-100 bg-white text-slate-700'
                    }`}
                  >
                    {message.text}
                  </div>
                </motion.div>
              ))}
              {isLoading && <TypingIndicator />}
              <div ref={bottomRef} />
            </div>

            {/* Formulario de entrada */}
            <form
              onSubmit={handleSubmit}
              className="border-t border-slate-100 bg-white p-4 sm:p-5"
            >
              <div className="flex items-end gap-3 rounded-3xl border border-slate-200 bg-slate-50 p-2 shadow-sm focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100">
                <textarea
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={2}
                  disabled={isLoading}
                  placeholder="Pregunta sobre educación, salud, economía..."
                  className="min-h-[52px] flex-1 resize-none bg-transparent px-3 py-2 text-sm text-slate-700 outline-none placeholder:text-slate-400 disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={isLoading || !inputValue.trim()}
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 text-white transition-transform hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Enviar pregunta a Gemini"
                >
                  <Send className="h-5 w-5" />
                </button>
              </div>
              <p className="mt-3 text-[11px] leading-5 text-slate-400">
                Gemini responde solo con datos de los planes oficiales de los candidatos. Enter para enviar.
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
